import { Link } from 'react-router-dom';
import { ArrowRight, Mic } from 'lucide-react';
import { PROGRAMS } from '../data/programs';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/1jxg5966_grief%20coaching%20logo.png";
const DR_GRIFFIN_PHOTO = "https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/xfj6pmgv_Gemini_Generated_Image_twtrvgtwtrvgtwtr.png";

export default function Home() {
  const programs = PROGRAMS;

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-pattern" data-testid="hero-section">
        <div className="absolute inset-0 grain-overlay" />
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="animate-fade-in-up">
                <img 
                  src={LOGO_URL} 
                  alt="Dr. Angelia Griffin Grief Coaching" 
                  className="h-24 md:h-32 w-auto mb-8"
                />
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1B365D] leading-tight animate-fade-in-up animation-delay-100">
                Transform Your Grief Into
                <span className="block text-[#2D7D7B]">Renewed Purpose</span>
              </h1>
              
              <p className="text-lg md:text-xl text-[#1B365D]/80 leading-relaxed max-w-xl animate-fade-in-up animation-delay-200">
                Dr. Griffin's comprehensive framework guides you through loss with clarity, movement, and meaning turning pain into purpose.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
                <Link to="/programs" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-cta-primary">
                  Explore Programs
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/about" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
                  Meet Dr. Griffin
                </Link>
              </div>
            </div>
            
            {/* Right Content - Image */}
            <div className="lg:col-span-5 animate-fade-in animation-delay-400">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A962]/20 to-transparent rounded-3xl" />
                <img 
                  src={DR_GRIFFIN_PHOTO}
                  alt="Dr. Angelia Griffin"
                  className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Dr. Griffin */}
      <section className="py-24 bg-[#1B365D] relative" data-testid="mission-section">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              Your Guide Through Grief
            </span>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-relaxed">
              Dr. Angelia Griffin provides a warm, encouraging space for people who feel stuck or overwhelmed by loss. Guided by her own experience with deep grief, she ensures no one navigates it alone. Through compassionate conversation, thoughtful assessment, and a clear path forward, she helps her clients feel heard, supported, and ready to reclaim life with meaning and purpose.
            </p>
            <div className="section-divider mx-auto mt-8" />
          </div>
        </div>
      </section>

      {/* Grief Wheel Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="grief-wheel-section">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                The Framework
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B365D] mb-6">
                Dr. A's Grief Wheel
              </h2>
              <p className="text-lg text-[#1B365D]/70 max-w-2xl mx-auto">
                How to Navigate Your Grief Journey from Pain to Purpose
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/frtl73ma_Gemini_Generated_Image_ikgopjikgopjikgo.png"
                alt="Dr. A's Grief Wheel - How to Navigate My Grief Journey from Pain to Purpose"
                className="w-full max-w-3xl mx-auto rounded-2xl shadow-xl"
              />
            </div>
            
            <p className="text-center text-[#1B365D]/60 mt-8 text-sm max-w-2xl mx-auto">
              Dr. Griffin's comprehensive framework addresses all types of grief—from anticipatory and chronic to suppressed and communal—guiding you through each stage with proven strategies.
            </p>
            
            <div className="text-center mt-10">
              <a href="https://griefassessment.agcthefixer.info/firststep" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 text-lg" data-testid="grief-assessment-btn">
                Take The Grief Assessment
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="py-24 md:py-32 bg-[#FAFAF8]" data-testid="programs-overview-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              The M.O.V.E.™ Course System
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B365D] mb-6">
              One Framework, Tailored to Your Loss
            </h2>
            <p className="text-lg text-[#1B365D]/70">
              From a single introductory workshop to the flagship cohort, choose the structured path
              that meets you exactly where you are.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <Link 
                to={`/programs/${program.slug}`}
                key={program.slug}
                className="service-card group relative overflow-hidden flex flex-col"
                data-testid={`program-card-${program.slug}`}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${program.color}`} />
                <div className="pt-4 flex flex-col h-full">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#C9A962]">
                      {program.badge}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#1B365D] mb-2 group-hover:text-[#2D7D7B] transition-colors">
                    {program.name}
                  </h3>
                  <p className="text-sm text-[#1B365D]/60 mb-3">{program.duration}</p>
                  <p className="text-[#1B365D]/75 leading-relaxed mb-6 flex-grow">
                    {program.tagline}
                  </p>
                  <span className="text-[#2D7D7B] font-medium flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                    View Program <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs" className="btn-secondary inline-flex items-center gap-2" data-testid="view-all-programs-cta">
              View All Programs
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-teaser-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                Meet Your Guide
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-6">
                Dr. Angelia Griffin
              </h2>
              <p className="text-lg text-[#1B365D]/70 leading-relaxed mb-6">
                A Grief Coach, Educator, and Keynote Speaker who helps high-functioning leaders and communities transform loss into clarity, movement, and renewed purpose.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-[#1B365D]">
                  <div className="w-2 h-2 rounded-full bg-[#C9A962]" />
                  30+ years of leadership experience
                </li>
                <li className="flex items-center gap-3 text-[#1B365D]">
                  <div className="w-2 h-2 rounded-full bg-[#C9A962]" />
                  20+ years in higher education
                </li>
                <li className="flex items-center gap-3 text-[#1B365D]">
                  <div className="w-2 h-2 rounded-full bg-[#C9A962]" />
                  Founder of IGMA, Inc. (501c3) & AGC LLC
                </li>
                <li className="flex items-center gap-3 text-[#1B365D]">
                  <div className="w-2 h-2 rounded-full bg-[#C9A962]" />
                  Author of "Turning In Is The Only Way Out"
                </li>
              </ul>
              <Link to="/about" className="btn-primary inline-flex items-center gap-2" data-testid="about-teaser-cta">
                Read Full Bio
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-full h-full border-2 border-[#C9A962]/30 rounded-2xl" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/jwwh3bqi_Dr.%20A%20Bio%20%281%29.png"
                  alt="Dr. Angelia Griffin"
                  className="relative rounded-2xl shadow-xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking CTA */}
      <section className="py-24 bg-[#2D7D7B] relative" data-testid="speaking-cta-section">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <Mic className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mb-2">
                  Book Dr. Griffin to Speak
                </h3>
                <p className="text-white/80">
                  Powerful keynotes on grief, resilience, and finding purpose after loss.
                </p>
              </div>
            </div>
            <Link to="/speaking" className="btn-secondary border-white text-white hover:bg-white hover:text-[#2D7D7B] whitespace-nowrap" data-testid="speaking-cta-button">
              View Speaking Topics
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#F5F5F0]" data-testid="final-cta-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B365D] mb-6">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-lg text-[#1B365D]/70 mb-8">
              Take the first step toward transforming your grief into renewed purpose. Dr. Griffin is here to guide you.
            </p>
            <a href="https://griefassessment.agcthefixer.info/firststep" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 text-lg" data-testid="final-cta-button">
              Take The Grief Assessment
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
